import { Grid } from '@material-ui/core';
import { Button, StandardDialog, TextField } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { bbox, center, circle, featureCollection } from '@turf/turf';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapComponent, {
  Layer,
  LayerProps,
  Marker,
  NavigationControl,
  Source,
} from 'react-map-gl';
import RoomIcon from '@material-ui/icons/Room';
import {
  InspectionData,
  InspectionDetails,
  InspectionPhoto,
} from '../types/types';
import { Box, makeStyles, Typography } from '@material-ui/core';

interface Location {
  lat: number;
  lng: number;
}

export const PickLocationDialog = observer(({ open, onClose }) => {
  const [mapViewCenter, setMapViewCenter] = useState<Location>();
  const [location, setLocation] = useState<Location>();

  const { environmentStore, inspectionStore } = useStore();
  const { inspectionPhotosStore } = inspectionStore;
  const { inspectionDetails } = inspectionPhotosStore;

  useEffect(() => {
    if (open && inspectionDetails) {
      const loc = {
        lat: inspectionDetails.latitude,
        lng: inspectionDetails.longitude,
      };
      setMapViewCenter(loc);
      setLocation(loc);
    }
  }, [open, inspectionDetails]);

  const dialogContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography>
          Move the marker or double click to set the location.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <CoordinatesInputs
          location={location}
          onChange={loc => {
            setLocation(loc);
            setMapViewCenter(loc);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        {location && (
          <div style={{ height: '50vh' }}>
            <Map
              location={location}
              mapViewCenter={mapViewCenter}
              onChange={loc => {
                setLocation(loc);
              }}
              radius={inspectionDetails?.allowedRadiusM / 1000}
            />
          </div>
        )}
      </Grid>
    </Grid>
  );

  const dialogActions = (
    <>
      <Button
        style={{ marginRight: '8px' }}
        onClick={() => onClose()}
        color="primary"
        variant="outlined"
        testId="cancel"
      >
        Cancel
      </Button>
      <Button
        testId={'save'}
        onClick={async () => {
          await inspectionPhotosStore.updateInspectionGeolocation(
            location.lat,
            location.lng
          );
          onClose();
        }}
        variant="contained"
        type="submit"
        color="primary"
      >
        Save
      </Button>
    </>
  );

  return (
    <StandardDialog
      title="Select Location"
      removeContentBox
      open={open}
      handleClose={onClose}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
    />
  );
});

interface InputsProps {
  location: Location;
  onChange: (location: Location) => void;
}

const CoordinatesInputs = observer(({ location, onChange }: InputsProps) => {
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  useEffect(() => {
    if (
      location?.lat &&
      location?.lng &&
      location.lat.toString() != latitude &&
      location.lng.toString() != longitude
    ) {
      setLatitude((location.lat ?? '').toString());
      setLongitude((location.lng ?? '').toString());
    }
  }, [location]);

  const changeLocation = () => {
    onChange({
      lat: parseFloat(latitude),
      lng: parseFloat(longitude),
    });
  };

  return (
    <Box display="flex">
      <Box pr={2} flex={1}>
        <TextField
          testId="latitude"
          variant="outlined"
          label="Latitude"
          placeholder="(as displayed in card)"
          value={latitude}
          type="number"
          onChange={e => setLatitude(e.target.value)}
          fullWidth
        />
      </Box>
      <Box pr={2} flex={1}>
        <TextField
          testId="longitude"
          variant="outlined"
          label="Longitude"
          type="number"
          value={longitude}
          onChange={e => setLongitude(e.target.value)}
          fullWidth
        />
      </Box>
      <Button
        testId="change-location"
        variant="outlined"
        color="primary"
        onClick={changeLocation}
      >
        Go
      </Button>
    </Box>
  );
});

const SP_MARKER_COLOR = '#FB8C00';

interface MapProps {
  location: Location;
  mapViewCenter: Location;
  onChange: (location: Location) => void;
  radius: number;
}

const Map = observer(
  ({ location, mapViewCenter, radius, onChange }: MapProps) => {
    const mapRef = useRef(null);
    const { environmentStore } = useStore();
    const [mapLoaded, setMapLoaded] = useState(false);
    const token = environmentStore.environmentVariables.mapBoxToken;

    useEffect(() => {
      if (mapLoaded && mapRef?.current) {
        const map = mapRef?.current;
        map.flyTo({
          center: [mapViewCenter.lng, mapViewCenter.lat],
        });
      }
    }, [mapLoaded, mapViewCenter]);

    const handleMapClick = useCallback(event => {
      const isDoubleClick = event.originalEvent.detail === 2;

      if (isDoubleClick) {
        onChange(event.lngLat);
      }
    }, []);

    return (
      <MapComponent
        ref={mapRef}
        initialViewState={{
          latitude: location.lat,
          longitude: location.lng,
          zoom: 17,
        }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={token}
        interactiveLayerIds={['inspection-photo-circles']}
        onClick={handleMapClick}
        onLoad={() => setMapLoaded(true)}
        doubleClickZoom={false}
      >
        <Marker
          latitude={location.lat}
          longitude={location.lng}
          draggable
          onDrag={e => onChange(e.lngLat)}
        >
          <RoomIcon style={{ fontSize: 40, color: SP_MARKER_COLOR }} />
        </Marker>
      </MapComponent>
    );
  }
);
